import { Button, Modal, TextField, useToast } from '@citizen-walk/shared-ui';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import type { PrimaryAxisResolution } from './streetSurveyAxisTypes';
import type { RoleTypeAxisResolution } from './streetSurveyAxisTypes';
import { computeAxisCounts, computeEvaluationSummary } from './computeAreaAxisCounts';
import {
  StreetSurveyAggregationPanel,
  toPrimaryAxis,
  type AggregationSelection,
} from './StreetSurveyAggregationPanel';
import type { ResolvedSurveyAxis } from './useStreetSurveyConfig';
import type { EvaluationAxisDef } from '@citizen-walk/shared-types';
import { saveRouteReportSnapshot } from './routeReportSnapshotActions';
import { computePolylineLengthM, distanceToPolylineM } from './streetSurveyRouteGeometry';
import { resolveMapLegendAxis, resolvePostCategoryCounts } from './streetSurveyPinDisplay';
import { resolveDonutSegments } from './streetSurveyClusterDonut';
import { RouteReportPreviewMap } from './RouteReportPreviewMap';

/** 近接判定の閾値 (m)。既定30m、5m単位。 */
const DEFAULT_THRESHOLD_M = 30;
const THRESHOLD_MIN_M = 5;
const THRESHOLD_MAX_M = 200;

export interface RouteReportModalProps {
  districtId: string;
  /** 集計している調査 (docs/98 R-97)。保存と詳細レポートのリンクに使う。 */
  surveyKey?: string;
  /** 詳細レポートのリンクに付ける検索文字列 (例 `?survey=bousai`)。 */
  detailQuery?: string;
  /** 地区の全解析済み地点 (近接判定の対象母集団)。 */
  posts: StreetSurveyPost[];
  /** 「既存地点から路線を作成」モードで描いた線の頂点 (`StreetSurveyResultsMap` の onRouteDrawn 経由)。 */
  lineCoordinates: { latitude: number; longitude: number }[];
  primaryAxis: PrimaryAxisResolution;
  roleTypeAxis?: RoleTypeAxisResolution;
  /** docs/27 §9: 評価軸 (無ければ内訳なし) と集計に使った調査項目の key。 */
  evaluation?: EvaluationAxisDef | null;
  axisKey?: string | null;
  /** 選べる調査項目 (この画面で集計項目・マトリクスの軸を切り替えるため、docs/27 §9-3)。 */
  surveyAxes?: ResolvedSurveyAxis[];
  onClose: () => void;
}

/**
 * 「既存地点から路線を作成」モーダル (docs/25 §10-6 拡張、2026-07-21)。
 * `AreaReportModal.tsx` を土台に、新規AI解析を行わず既存の解析済み地点から路線を作る専用実装。
 * 近接判定の閾値をユーザーが調整でき、変更するとマッチする地点数がリアルタイムに変わる。
 * AI要約・PDF出力は無し（一括解析由来の路線保存にも無い機能のため、一貫性のため付けない）。
 */
export function RouteReportModal({
  districtId,
  posts,
  lineCoordinates,
  primaryAxis,
  roleTypeAxis,
  onClose,
  surveyKey,
  detailQuery = '',
  evaluation = null,
  axisKey = null,
  surveyAxes = [],
}: RouteReportModalProps) {
  const { toast } = useToast();
  const { state: authState } = useAdminAuth();
  const [thresholdM, setThresholdM] = useState(DEFAULT_THRESHOLD_M);
  const [savingName, setSavingName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const lengthM = useMemo(() => computePolylineLengthM(lineCoordinates), [lineCoordinates]);

  const matchedPosts = useMemo(
    () => posts.filter((p) => distanceToPolylineM(p.location, lineCoordinates) <= thresholdM),
    [posts, lineCoordinates, thresholdM],
  );

  // 集計する調査項目はこの画面で切り替えられる (保存もその項目の集計になる)。
  const [agg, setAgg] = useState<AggregationSelection>({
    rowAxisKey: axisKey,
    colAxisKey: null,
    granularity: 'category',
    view: 'bar',
    splitByPolarity: false,
  });
  const aggAxis = surveyAxes.find((a) => a.key === agg.rowAxisKey) ?? null;
  const axes = useMemo(
    () =>
      computeAxisCounts(
        matchedPosts,
        aggAxis ? toPrimaryAxis(aggAxis) : primaryAxis,
        evaluation,
        agg.granularity,
      ),
    [matchedPosts, aggAxis, primaryAxis, evaluation, agg.granularity],
  );
  const evaluationSummary = computeEvaluationSummary(matchedPosts, evaluation);

  // 結果地図・エリア選択集計と同じ軸解決でドーナツを描く (§10-3参照)。
  const legendAxis = resolveMapLegendAxis(primaryAxis, roleTypeAxis);
  const previewMapPoints = matchedPosts.map((p, i) => ({
    id: p.id,
    lat: p.location.latitude,
    lng: p.location.longitude,
    number: i + 1,
    segments: legendAxis
      ? resolveDonutSegments(resolvePostCategoryCounts(p, legendAxis.axis), legendAxis.axis)
      : [],
  }));

  async function handleSaveSnapshot() {
    const name = savingName?.trim();
    if (!name || !authState.user || saving) return;
    setSaving(true);
    try {
      await saveRouteReportSnapshot(
        {
          districtId,
          name,
          lineCoordinates,
          lengthM,
          postIds: matchedPosts.map((p) => p.id),
          pointCount: matchedPosts.length,
          axisCounts: axes,
          axisKey: agg.rowAxisKey ?? axisKey,
          evaluation: evaluationSummary,
          summary: null,
          surveyKey,
        },
        authState.user.uid,
      );
      toast('success', `「${name}」として保存しました。`);
      onClose();
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`既存地点から路線を作成（${matchedPosts.length}地点検出）`}
      size="xl"
    >
      <div className="flex items-center justify-end mb-4">
        <p className="text-xs text-slate-500">路線の距離: 約{Math.round(lengthM)}m</p>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <label className="text-sm font-semibold text-slate-700" htmlFor="route-threshold">
          近接判定の閾値:
        </label>
        <input
          id="route-threshold"
          type="number"
          min={THRESHOLD_MIN_M}
          max={THRESHOLD_MAX_M}
          step={5}
          value={thresholdM}
          onChange={(e) => {
            const raw = Math.round(Number(e.target.value) / 5) * 5;
            setThresholdM(
              Math.max(THRESHOLD_MIN_M, Math.min(THRESHOLD_MAX_M, raw || THRESHOLD_MIN_M)),
            );
          }}
          className="w-20 border border-slate-300 rounded px-2 py-1 text-sm"
        />
        <span className="text-xs text-slate-500">
          m（5m単位、既定{DEFAULT_THRESHOLD_M}
          m。線からこの距離以内にある地点を路線として検出します）
        </span>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-700">
          検出された地点: <strong>{matchedPosts.length}件</strong>
        </p>
        <div className="flex items-center gap-2">
          {savingName === null ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={matchedPosts.length === 0}
              onClick={() => setSavingName('')}
            >
              路線として保存
            </Button>
          ) : (
            <div className="flex items-end gap-2 bg-slate-50 rounded px-3 py-2">
              <TextField
                label="路線名"
                value={savingName}
                onChange={setSavingName}
                placeholder="例: 赤門〜町内会館"
                className="flex-1"
              />
              <Button
                size="sm"
                disabled={!savingName.trim() || saving}
                onClick={() => void handleSaveSnapshot()}
              >
                {saving ? '保存中…' : '保存する'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={saving}
                onClick={() => setSavingName(null)}
              >
                キャンセル
              </Button>
            </div>
          )}
        </div>
      </div>

      <RouteReportPreviewMap
        points={previewMapPoints}
        lineCoordinates={lineCoordinates}
        className="w-full h-56 rounded border border-slate-200 mb-4"
      />

      {matchedPosts.length === 0 ? (
        <p className="text-sm text-slate-500">
          閾値内に解析済みの地点が見つかりませんでした。閾値を広げてみてください。
        </p>
      ) : (
        <>
          <div className="mb-4">
            <h3 className="text-sm font-bold text-slate-700 mb-2">検出要素の集計</h3>
            <StreetSurveyAggregationPanel
              posts={matchedPosts}
              axes={surveyAxes}
              evaluation={evaluation}
              initialRowAxisKey={agg.rowAxisKey}
              onChange={setAgg}
            />
          </div>

          <h3 className="text-sm font-bold text-slate-700 mt-5 mb-2">検出された地点一覧</h3>
          <ul className="grid grid-cols-1 gap-2 max-h-60 overflow-auto">
            {matchedPosts.map((p, i) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs text-slate-400 shrink-0">{i + 1}.</span>
                  <span className="text-xs text-slate-600 truncate">{p.description}</span>
                </div>
                <Link
                  to={`/districts/${districtId}/street-survey/${p.id}${detailQuery}`}
                  className="text-xs text-primary-600 hover:underline shrink-0"
                >
                  詳細を見る →
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}
